import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { useNotification } from "../../context/NotificationContext";

const UMLogin = () => {
  const { t, i18n } = useTranslation("auth");
  const { login } = useUniAdminAuth();
  const { showError } = useNotification();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const toggleLanguage = () => {
    const nextLang = i18n.language.startsWith("en") ? "tr" : "en";
    i18n.changeLanguage(nextLang);
    window.localStorage.setItem("i18nextLng", nextLang);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || !email || !password) return;
    setLoading(true);
    try {
      const result = await login(email, password, rememberMe);
      if (result.success) {
        navigate("/uni-admin/dashboard", { replace: true });
      } else {
        showError(result.error || t("login.failed", "Login failed. Please check your credentials."));
      }
    } catch {
      showError(t("login.unexpectedError", "An unexpected error occurred."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light p-3 p-md-4 p-lg-5">
      <div className="card border-0 shadow-lg w-100 overflow-hidden" style={{ maxWidth: "1100px", borderRadius: "20px" }}>
        <div className="row g-0">
          
          {/* Left Side - Form */}
          <div className="col-12 col-lg-6 bg-white p-4 p-md-5 d-flex flex-column position-relative">
            {/* Language Switcher */}
            <div className="d-flex justify-content-end mb-4">
              <button 
                onClick={toggleLanguage} 
                className="btn btn-sm btn-light rounded-pill px-3 fw-medium border"
                style={{ transition: "all 0.2s" }}
              >
                <i className="fas fa-globe me-2 text-primary"></i>
                {i18n.language.startsWith("en") ? "Türkçe" : "English"}
              </button>
            </div>

            <div className="flex-grow-1 d-flex flex-column justify-content-center w-100 mx-auto" style={{ maxWidth: "400px" }}>
              <div className="mb-5">
                <div className="d-flex align-items-center mb-4">
                  <div className="me-2" style={{ width: "32px", height: "32px" }}>
                    <img src="/favicon/logo192.png" alt="The Uniflow Logo" className="img-fluid rounded" />
                  </div>
                  <span className="fw-bold fs-5 text-dark">The Uniflow</span>
                </div>
                
                <h2 className="fw-bold text-dark mb-2" style={{ fontSize: "2rem", letterSpacing: "-0.5px" }}>
                  {t("login.title", "Log in to your Account")}
                </h2>
                <p className="text-muted">
                  {t("login.subtitle", "Welcome back! Please enter your details.")}
                </p>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0 text-muted px-3">
                      <i className="far fa-envelope"></i>
                    </span>
                    <input
                      type="email"
                      className="form-control border-start-0 ps-0 py-2"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("login.emailPlaceholder", "Email")}
                      required
                      autoFocus
                      style={{ boxShadow: "none", borderColor: "#dee2e6" }}
                    />
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0 text-muted px-3">
                      <i className="fas fa-lock"></i>
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control border-start-0 border-end-0 ps-0 py-2"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t("login.passwordPlaceholder", "Password")}
                      required
                      style={{ boxShadow: "none", borderColor: "#dee2e6" }}
                    />
                    <button
                      type="button"
                      className="btn btn-white bg-white border text-muted border-start-0 px-3"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      style={{ borderColor: "#dee2e6" }}
                    >
                      <i className={`far ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                    </button>
                  </div>
                </div>
                
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="rememberMe"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <label className="form-check-label text-muted small" htmlFor="rememberMe">
                      {t("login.rememberMe", "Remember me")}
                    </label>
                  </div>
                  <Link to="/uni-admin/forgot-password" className="text-decoration-none small fw-medium" style={{ color: "#0d6efd" }}>
                    {t("login.forgotPassword", "Forgot Password?")}
                  </Link>
                </div>
                
                <button
                  type="submit"
                  className="btn btn-primary w-100 py-2 fw-medium auth-btn rounded-3"
                  disabled={loading || !email || !password}
                  style={{ backgroundColor: "#0d6efd", border: "none" }}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      {t("login.submitting", "Logging in...")}
                    </>
                  ) : (
                    t("login.signInButton", "Log In")
                  )}
                </button>
              </form>
            </div>
          </div>
          
          {/* Right Side - Blue Banner */}
          <div className="col-12 col-lg-6 d-none d-lg-flex flex-column align-items-center justify-content-center text-white position-relative" style={{ backgroundColor: "#0d6efd", minHeight: "600px" }}>
            {/* Abstract Background Circles */}
            <div className="position-absolute rounded-circle" style={{ width: "500px", height: "500px", border: "60px solid rgba(255,255,255,0.05)", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}></div>
            <div className="position-absolute rounded-circle" style={{ width: "350px", height: "350px", border: "40px solid rgba(255,255,255,0.05)", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}></div>
            
            {/* Center Illustration */}
            <div className="position-relative z-1 mb-5">
              <div className="bg-white rounded-3 shadow-lg p-3 mx-auto" style={{ width: "240px" }}>
                <div className="d-flex align-items-center mb-3">
                  <div className="d-flex gap-1 me-3">
                    <div className="rounded-circle bg-danger" style={{ width: "8px", height: "8px" }}></div>
                    <div className="rounded-circle bg-warning" style={{ width: "8px", height: "8px" }}></div>
                    <div className="rounded-circle bg-success" style={{ width: "8px", height: "8px" }}></div>
                  </div>
                  <div className="bg-light rounded-pill w-100" style={{ height: "6px" }}></div>
                </div>
                {/* Mock rows */}
                {[1, 2, 3].map(i => (
                  <div key={i} className="d-flex align-items-center p-2 mb-2 border rounded">
                    <div className="rounded-circle bg-light d-flex align-items-center justify-content-center me-2" style={{ width: "24px", height: "24px" }}>
                      <i className="fas fa-user text-muted" style={{ fontSize: "10px" }}></i>
                    </div>
                    <div className="w-100">
                      <div className="bg-light rounded mb-1" style={{ height: "6px", width: "80%" }}></div>
                      <div className="bg-light rounded" style={{ height: "4px", width: "40%" }}></div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Floating elements */}
              <div className="position-absolute bg-white rounded-circle shadow d-flex align-items-center justify-content-center float-icon-1" style={{ width: "50px", height: "50px", top: "20%", left: "-30px" }}>
                <i className="fas fa-calendar-check text-primary fs-5"></i>
              </div>
              <div className="position-absolute bg-white rounded-circle shadow d-flex align-items-center justify-content-center float-icon-2" style={{ width: "45px", height: "45px", bottom: "30%", right: "-20px" }}>
                <i className="fas fa-chart-pie text-success fs-5"></i>
              </div>
              <div className="position-absolute bg-white rounded-circle shadow d-flex align-items-center justify-content-center float-icon-3" style={{ width: "40px", height: "40px", bottom: "10%", left: "10%" }}>
                <i className="fas fa-users text-warning fs-5"></i>
              </div>
            </div>
            
            <div className="text-center mt-4 px-5 position-relative z-1">
              <h3 className="fw-bold mb-3">{t("marketing.loginTitle", "Manage everything effortlessly.")}</h3>
              <p className="text-white-50 px-4">
                {t("marketing.loginSubtitle", "All your university events, clubs, and students in one easily customizable dashboard.")}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        .input-group-text {
          border-color: #dee2e6;
        }
        .form-control:focus {
          border-color: #dee2e6;
          box-shadow: none;
        }
        .input-group:focus-within {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
          border-radius: 0.375rem;
        }
        .input-group:focus-within .input-group-text,
        .input-group:focus-within .form-control,
        .input-group:focus-within .btn {
          border-color: #0d6efd !important;
        }
        .auth-btn:hover:not(:disabled) {
          background-color: #0b5ed7 !important;
        }
        @keyframes floating {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        .float-icon-1 { animation: floating 3s ease-in-out infinite; }
        .float-icon-2 { animation: floating 4s ease-in-out infinite; animation-delay: 1s; }
        .float-icon-3 { animation: floating 3.5s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default UMLogin;
